"use client";

import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";

import { ApiError, api, loginUrl } from "@/lib/api/client";
import type { Job } from "@/lib/api/types";

export const POLL_MS = 1000;
const FINISHED = new Set(["succeeded", "failed", "cancelled"]);

export function isFinished(job: Job | null): boolean {
  return job !== null && FINISHED.has(job.status);
}

/** Fragt den Job regelmäßig ab, bis er einen Endstatus erreicht (bei Freigabe-Wartezeit weiter). */
export function useJob(jobId: number) {
  const router = useRouter();
  const [job, setJob] = useState<Job | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const finished = isFinished(job);

  useEffect(() => {
    let cancelled = false;
    api<Job>(`/api/jobs/${jobId}`)
      .then((next) => {
        if (cancelled) return;
        setJob(next);
        setError(null);
      })
      .catch((caught: unknown) => {
        if (cancelled) return;
        if (caught instanceof ApiError && caught.status === 401) {
          router.replace(loginUrl(window.location.pathname + window.location.search));
          return;
        }
        setError(caught instanceof Error ? caught.message : "Unbekannter Fehler");
      });
    return () => {
      cancelled = true;
    };
  }, [jobId, tick, router]);

  useEffect(() => {
    if (finished) return;
    const timer = window.setInterval(() => setTick((t) => t + 1), POLL_MS);
    return () => window.clearInterval(timer);
  }, [finished]);

  return { job, error, setJob };
}
